import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as TimelineService from '_services/timeline-service';

/**
 * Create a gate relocation task
 * @param {Object} params - Task parameters
 * @param {string} params.iata - Airport IATA code
 * @param {Object} params.flight - Flight object
 * @param {Object} params.passenger - Passenger object
 * @param {Object} params.pickupLocation - Pickup location
 * @param {Object} params.dropoffLocation - Dropoff location
 * @param {string} params.pickupTime - Optional pickup time
 * @param {string} params.relocationReason - Optional reason
 * @param {string} params.notes - Optional notes
 */
export const createGateRelocation = createAsyncThunk(
    'gateRelocationForm/createGateRelocation',
    async (params, { rejectWithValue }) => {
        try {
            const result = await TimelineService.createGateRelocationTask(params);
            return result;
        } catch (error) {
            return rejectWithValue(error.message || 'Failed to create gate relocation task');
        }
    }
);

const initialState = {
    // Selected items (local copies for form submission)
    selectedFlight: null,
    selectedPassenger: null,
    
    // Form data
    formData: {
        flightId: null,
        passengerId: null,
        newGate: '',
        relocationReason: 'Gate Change',
        notes: '',
    },
    
    // Submission status
    submitStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    submitError: null,
    
    // Created task (result from API)
    createdTask: null,
};

const gateRelocationFormSlice = createSlice({
    name: 'gateRelocationForm',
    initialState,
    reducers: {
        // Reset the entire form
        resetGateRelocationForm: (state) => {
            state.selectedFlight = null;
            state.selectedPassenger = null;
            state.formData = initialState.formData;
            state.submitStatus = 'idle';
            state.submitError = null;
            state.createdTask = null;
        },
        
        // Set selected flight
        setGateRelocationFlight: (state, action) => {
            state.selectedFlight = action.payload;
            state.formData.flightId = action.payload?._id || null;
        },
        
        // Set selected passenger
        setGateRelocationPassenger: (state, action) => {
            state.selectedPassenger = action.payload;
            state.formData.passengerId = action.payload?._id || null;
        },
        
        // Update form data
        updateGateRelocationFormData: (state, action) => {
            state.formData = { ...state.formData, ...action.payload };
        },
        
        // Clear submit status (for retry)
        clearGateRelocationSubmitStatus: (state) => {
            state.submitStatus = 'idle';
            state.submitError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createGateRelocation.pending, (state) => {
                state.submitStatus = 'loading';
                state.submitError = null;
            })
            .addCase(createGateRelocation.fulfilled, (state, action) => {
                state.submitStatus = 'succeeded';
                state.createdTask = action.payload;
            })
            .addCase(createGateRelocation.rejected, (state, action) => {
                state.submitStatus = 'failed';
                state.submitError = action.payload || 'Failed to create gate relocation task';
            });
    },
});

// Export actions
export const { 
    resetGateRelocationForm,
    setGateRelocationFlight,
    setGateRelocationPassenger,
    updateGateRelocationFormData,
    clearGateRelocationSubmitStatus,
} = gateRelocationFormSlice.actions;

// Export reducer
export default gateRelocationFormSlice.reducer;

// Selectors
export const selectGateRelocationFlight = (state) => state.gateRelocationForm.selectedFlight;
export const selectGateRelocationPassenger = (state) => state.gateRelocationForm.selectedPassenger;
export const selectGateRelocationFormData = (state) => state.gateRelocationForm.formData;
export const selectGateRelocationSubmitStatus = (state) => state.gateRelocationForm.submitStatus;
export const selectGateRelocationSubmitError = (state) => state.gateRelocationForm.submitError;
export const selectGateRelocationCreatedTask = (state) => state.gateRelocationForm.createdTask;

// Derived selectors
export const selectIsGateRelocationSubmitting = (state) => state.gateRelocationForm.submitStatus === 'loading';
export const selectIsGateRelocationSuccess = (state) => state.gateRelocationForm.submitStatus === 'succeeded';
